import { FC, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';

import { ToggleButton } from '@rikstv/shared-components';

import { wishListToggleAction } from '../../forces/wishlist/actions';
import { toggleWishList } from '../../forces/wishlist/api';
import { useAuthToken } from '../../hooks/useAuthToken';
import { AddedToListSvg, AddToListSvg, animateBounceClass } from '../../icons';
import { ErrorMessage } from '../errorMessage/ErrorMessage';
import { usePage } from '../pages/internal/usePage';
import { usePages } from '../pages/usePages';
import { useRemoveFromSwimlaneCache } from '../swimlane/utils/useRemoveFromSwimlaneCache';

import styles from './WishlistButton.module.css';

interface Props {
  wishlistUrl?: string;
  assetId: string;
  initialState?: boolean;
}

export const WishlistButton: FC<Props> = ({ wishlistUrl, assetId, initialState }) => {
  const { clearMyListCache } = useClearMyListCache();
  const { hasError, isLoading, onToggle, enabled, isFavorite } = useFavoriteToggle(
    assetId,
    wishlistUrl,
    initialState ?? false
  );

  if (!enabled) {
    return null;
  }

  const handleClick = () => {
    clearMyListCache();
    onToggle(!isFavorite);
  };

  return (
    <>
      <ToggleButton
        className={classNames(styles.wishlistButton)}
        defaultChecked={isFavorite}
        disabled={isLoading}
        aria-busy={isLoading}
        aria-live="polite"
        aria-label={getLabel(isFavorite)}
        onClick={handleClick}
        icons={{ toggleIcon: HeartToggle }}>
        Min liste
      </ToggleButton>

      <ErrorMessage show={hasError} title={false} leftAligned={true} message="Æsj, noe gikk galt. Prøv igjen!" />
    </>
  );
};

const getLabel = (checked: boolean) => (checked ? 'Fjern fra min liste' : 'Legg til i min liste');

const HeartToggle: FC<{ checked: boolean }> = ({ checked }) => (
  <span data-testid={checked ? 'heart' : 'unheart'} title={getLabel(checked)}>
    {checked ? <AddedToListSvg className={animateBounceClass} /> : <AddToListSvg />}
  </span>
);

const useClearMyListCache = () => {
  const { defaultPage } = usePages();
  const { page } = usePage(defaultPage?.link || '');
  const myListSwimlane = page?.swimlanes.find(swimlane => swimlane.type === 'MyList');
  const clearMyListCache = useRemoveFromSwimlaneCache(myListSwimlane?.link || '');
  return { clearMyListCache };
};

const useFavoriteToggle = (assetId: string, wishlistUrl: string | undefined, initialState: boolean) => {
  const token = useAuthToken();
  const dispatch = useDispatch();

  const [isFavorite, setIsFavorite] = useState(initialState);
  const [{ hasError, isLoading }, setReqState] = useState({ isLoading: false, hasError: false });
  const onToggle = useCallback(
    (isFavorite: boolean) => {
      if (token && wishlistUrl) {
        setReqState({ hasError: false, isLoading: true });
        toggleWishList(isFavorite, wishlistUrl, token)
          .then(() => {
            dispatch(wishListToggleAction(isFavorite));
            setIsFavorite(isFavorite);
          })
          .catch(() => setReqState(s => ({ ...s, hasError: true })))
          .finally(() => setReqState(s => ({ ...s, isLoading: false })));
      }
    },
    [dispatch, token, wishlistUrl]
  );

  return {
    onToggle,
    isLoading,
    hasError,
    enabled: wishlistUrl && assetId && token,
    isFavorite,
  };
};
